import { env } from '@/env'

/**
 * Where a workspace actually lives — the tenant-scoped frontend app.
 *
 * The picker used to link back to Passport (`{issuer}/{subdomain}`), which
 * dropped a user who had just signed in there back onto the sign-in surface.
 * With no client in play Passport has nowhere to send them but the Lobby, so
 * picking a workspace looped to the page they picked it from. The app is the
 * real destination: it opens its own OAuth request, and Passport — already
 * holding a live session — answers with a code immediately, so the hop is
 * invisible.
 *
 * The host per environment is a product fact, not a deploy-time secret, so it
 * lives here keyed on VITE_APP_ENV rather than in four .env files that can
 * silently drift. VITE_TENANT_APP_URL overrides it, so a developer running the
 * frontend on another port doesn't have to patch source.
 */
const TEMPLATE_BY_ENV = {
  development: 'http://{subdomain}.localhost:4201',
  staging: 'https://{subdomain}.spaceti.eu',
  uat: 'https://{subdomain}.spaceti.co',
  production: 'https://{subdomain}.spaceti.com',
} as const

export function tenantAppUrl(subdomain: string): string {
  const template = env.VITE_TENANT_APP_URL || TEMPLATE_BY_ENV[env.VITE_APP_ENV]
  return template.replaceAll('{subdomain}', subdomain)
}

/**
 * Host of the same URL, for the line under the workspace name. Derived from
 * the URL rather than assembled a second time so the row can never advertise
 * one address and navigate to another.
 */
export function tenantAppHost(subdomain: string): string {
  return tenantAppUrl(subdomain).replace(/^https?:\/\//, '')
}
